import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import logger from 'ws-scripts/modules/logger';
import { isBrowser } from 'ws-scripts/modules/environment';
import { usePrefs, useFlags } from 'wsm-common-data';
import useEventBus from './useEventBus';
import {
	addPaymentTimeoutToNewRelic,
	addTotalTimeToNewRelic,
	addPaymentRenderToNewRelic,
	addPaymentMismatchToNewRelic,
	comparePaymentData,
	startTimeMarker,
	endTimeMarker,
	getPerformanceMeasure
} from '../helpers/logging-client';
import { ESNTIAL_PAYMENT_ERROR_MESSAGE } from '../constants';
import useErrorHandler from './useErrorHandler';
import {
	extractVehicleUUID,
	getCachedPaymentData
} from '../helpers/payment-cache-utils';
import {
	DEFAULT_PAYMENT_TIMEOUT_MS,
	CACHE_PRELOAD_FLAG,
	PAYMENT_OBJECT_FLAG,
	TIMEOUT_FLAG
} from './usePaymentData/constants';
import {
	processPaymentObjectUpdate,
	hasAnyValidPaymentInRawData
} from './usePaymentData/processors/paymentObjectProcessor';
import { processLegacyPayment } from './usePaymentData/processors/legacyPaymentProcessor';

const usePaymentData = () => {
	const { enableIncentives } = usePrefs();
	const [isLoading, setIsLoading] = useState(true);
	const [hasError, setHasError] = useState(false);
	const [udsPayments, setUdsPayments] = useState(null);
	const [paymentsData, setPaymentsData] = useState(null);
	const [hasReceivedData, setHasReceivedData] = useState(false); // Track if we've received any payment data
	const listingType = useSelector((state) => state.listingType);
	const [eventBus, isReady] = useEventBus();
	const paymentsRef = useRef(udsPayments);
	const errorTimeoutRef = useRef(null);
	const cachedPaymentDataRef = useRef(null);
	const hasRenderedCachedRef = useRef(false);
	const pendingPaymentRef = useRef(null);
	const isInitialCalcDoneRef = useRef(false);
	const initialEventCountRef = useRef(0);
	const flags = useFlags();
	const paymentTimeout =
		flags[TIMEOUT_FLAG] || DEFAULT_PAYMENT_TIMEOUT_MS;
	const usePaymentObjectUpdate =
		flags[PAYMENT_OBJECT_FLAG] || false;

	const hideOnEsntialPaymentError = useErrorHandler(
		setHasError,
		setIsLoading,
		ESNTIAL_PAYMENT_ERROR_MESSAGE
	);

	const processPaymentObjectEvent = (paymentArray = [], isFromCache = false) => {

		endTimeMarker('payment');
		const elapsedTime = getPerformanceMeasure('payment');

		logger.info(
			`WS-TRACK-MY-OFFER -> uds:PaymentObject:Update received: ${JSON.stringify(
				paymentArray,
				null,
				2
			)}`,
			{
				isFromCache,
				usePaymentObjectUpdate,
				featureFlag: 'ws-track-my-offer-use-payment-object-update',
				paymentArrayLength: paymentArray?.length || 0
			}
		);

		// Track event to New Relic
		if (window.newrelic && window.newrelic.addPageAction) {
			window.newrelic.addPageAction('paymentObjectUpdateReceived', {
				source: isFromCache ? 'cache' : 'fresh',
				paymentCount: paymentArray?.length || 0,
				featureFlag: 'ws-track-my-offer-use-payment-object-update',
				elapsedTime
			});
		}

		// Clear error timeout
		if (errorTimeoutRef.current) {
			logger.info(
				'WS-TRACK-MY-OFFER: Clearing error timeout - PaymentObject data received',
				{
					hadTimeout: true,
					paymentArray: paymentArray ? paymentArray.length : null
				}
			);
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		}

		// Process payment data
		const processedPayments = processPaymentObjectUpdate(paymentArray, enableIncentives);

		if (!processedPayments) {
			logger.error('WS-TRACK-MY-OFFER: Failed to process PaymentObject data - setting global error state');
			setHasError(true);
			setIsLoading(false);
			return;
		}

		// Handle cache vs fresh data tracking
		if (isFromCache) {
			cachedPaymentDataRef.current = processedPayments;
			hasRenderedCachedRef.current = true;
		} else if (hasRenderedCachedRef.current && cachedPaymentDataRef.current) {
			const mismatchData = comparePaymentData(
				cachedPaymentDataRef.current,
				processedPayments
			);
			if (mismatchData) {
				addPaymentMismatchToNewRelic(mismatchData);
			}
		}

		addPaymentRenderToNewRelic(
			isFromCache ? 'cache' : 'fresh',
			elapsedTime
		);

		if (!isFromCache) {
			addTotalTimeToNewRelic(elapsedTime, 'payment');
		}

		logger.info('WS-TRACK-MY-OFFER: Successfully applied PaymentObject data', {
			processedPaymentsCount: processedPayments.length,
			validPayments: processedPayments.filter(p => !p.hasError).length,
			errorPayments: processedPayments.filter(p => p.hasError).length,
			paymentDetails: processedPayments.map(p => ({
				label: p.paymentLabel,
				hasError: p.hasError,
				payment: p.payment,
				displayValue: p.hasError ? 'N/A' : `$${p.payment.toLocaleString()}`
			}))
		});

		setPaymentsData(processedPayments);
		setUdsPayments(paymentArray);
		setHasReceivedData(true); // Mark that we've received real payment data
		setIsLoading(false);
		setHasError(false);
	};

	// Flush buffered payment data to state
	const flushPendingPayment = () => {
		const pending = pendingPaymentRef.current;
		if (pending) {
			pendingPaymentRef.current = null;
			processPaymentObjectEvent(pending.paymentArray, pending.isFromCache);
		}
	};

	// PaymentObject:Update fires multiple times during initial UDS calculation.
	// Buffer first event until calcConfigUpdate:completed or second event arrives.
	const handlePaymentObjectUpdate = (paymentArray, isFromCache = false) => {
		// After initial calc, process immediately — no buffering needed
		if (isInitialCalcDoneRef.current) {
			processPaymentObjectEvent(paymentArray, isFromCache);
			return;
		}

		// Error pages: no valid payments → process immediately
		if (!hasAnyValidPaymentInRawData(paymentArray)) {
			logger.info(
				'WS-TRACK-MY-OFFER: No valid payments in event data — processing immediately (error page)'
			);
			isInitialCalcDoneRef.current = true;
			processPaymentObjectEvent(paymentArray, isFromCache);
			return;
		}

		initialEventCountRef.current += 1;

		// Always store the latest data
		pendingPaymentRef.current = { paymentArray, isFromCache };
		
		// Flush now instead of waiting for calcConfigUpdate:completed.
		if (initialEventCountRef.current >= 2) {
			logger.info(
				'WS-TRACK-MY-OFFER: 2nd PaymentObject:Update received — flushing settled data'
			);
			isInitialCalcDoneRef.current = true;
			flushPendingPayment();
			return;
		}

		// 1st event with valid data — buffer and wait for either
		// calcConfigUpdate:completed or a 2nd event to confirm.
		logger.info(
			'WS-TRACK-MY-OFFER: Buffering 1st PaymentObject:Update (waiting for completed signal or next event)'
		);
	};

	const handlePaymentUpdate = (paymentInfo, isFromCache = false) => {

		endTimeMarker('payment');
		const elapsedTime = getPerformanceMeasure('payment');

		logger.info(
			`DDC: WS-TRACK-MY-OFFER -> uds:PaymentCalcResponse:Update: ${JSON.stringify(
				paymentInfo,
				null,
				2
			)}`
		);

		// Check for cached data mismatch
		if (isFromCache) {
			cachedPaymentDataRef.current = paymentInfo;
			hasRenderedCachedRef.current = true;
		} else if (
			hasRenderedCachedRef.current &&
			cachedPaymentDataRef.current
		) {
			// Check for mismatch if we previously rendered cached data
			const mismatchData = comparePaymentData(
				cachedPaymentDataRef.current,
				paymentInfo
			);
			if (mismatchData) {
				addPaymentMismatchToNewRelic(mismatchData);
			}
		}

		// Track payment render (cacheHit flag set automatically based on renderSource)
		addPaymentRenderToNewRelic(
			isFromCache ? 'cache' : 'fresh',
			elapsedTime
		);

		// Avoid double-counting for cached renders
		if (!isFromCache) {
			addTotalTimeToNewRelic(elapsedTime, 'payment');
		}

		// Clear error timeout
		if (errorTimeoutRef.current) {
			logger.info(
				'WS-TRACK-MY-OFFER: Clearing error timeout - payment data received',
				{
					hadTimeout: true,
					paymentInfo: paymentInfo ? Object.keys(paymentInfo) : null
				}
			);
			clearTimeout(errorTimeoutRef.current);
			errorTimeoutRef.current = null;
		} else {
			logger.info(
				'WS-TRACK-MY-OFFER: Payment data received (no timeout to clear)',
				{
					paymentInfo: paymentInfo ? Object.keys(paymentInfo) : null
				}
			);
		}

		setUdsPayments(paymentInfo);
		setHasReceivedData(true); // Mark that we've received real payment data
		setIsLoading(false);
		const hasPaymentError =
			!!paymentInfo?.paymentErrorState &&
			!paymentInfo?.isFinanceErrorState &&
			!paymentInfo?.isLeaseErrorState;
		if (hasPaymentError) {
			logger.error('WS-TRACK-MY-OFFER: Payment error state detected', {
				paymentErrorState: paymentInfo?.paymentErrorState,
				paymentInfo
			});
		}
		setHasError(hasPaymentError);
	};

	// Check for cached payment data
	useEffect(() => {
		if (isBrowser && flags[CACHE_PRELOAD_FLAG]) {
			try {
				const uuid = extractVehicleUUID(window.location.pathname);
				const paymentData = getCachedPaymentData(uuid);

				if (paymentData) {
					logger.info(
						'WS-TRACK-MY-OFFER: Found cached payment data in localStorage, checking for errors before using',
						{ uuid, hasFinanceError: !!paymentData?.isFinanceErrorState, hasLeaseError: !!paymentData?.isLeaseErrorState }
					);

					// Skip cached data with all errors
					const hasAllErrors = paymentData?.isFinanceErrorState && paymentData?.isLeaseErrorState && !paymentData?.rate?.Excellent?.cashOffers;

					if (!hasAllErrors) {
						// Use cached data immediately - mark as from cache
						handlePaymentUpdate(paymentData, true);
					} else {
						logger.warn('WS-TRACK-MY-OFFER: Skipping cached data - all payment types have errors, would cause N/A flash');
					}
				}
			} catch (e) {
				// Ignore errors, continue normally
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags]);

	useEffect(() => {
		const pubsub = window?.DDC?.pubsub;
		if (isReady) {
			logger.info(
				'WS-TRACK-MY-OFFER: usePaymentData setting up event listeners',
				{
					isReady,
					flags: {
						preloadEnabled: flags[CACHE_PRELOAD_FLAG],
						paymentTimeout
					}
				}
			);
			startTimeMarker('payment');

			// Handle timeout
			errorTimeoutRef.current = setTimeout(() => {
				// Flush buffered data on timeout
				if (usePaymentObjectUpdate && pendingPaymentRef.current) {
					logger.info(
						'WS-TRACK-MY-OFFER: Payment timeout reached with buffered data — flushing'
					);
					isInitialCalcDoneRef.current = true;
					flushPendingPayment();
					errorTimeoutRef.current = null;
					return;
				}

				if (flags[CACHE_PRELOAD_FLAG]) {
					// Log warning instead of hiding widget - cached data may still arrive
					logger.warn(
						'WS-TRACK-MY-OFFER: Payment data is taking longer than expected',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
				} else if (!paymentsRef.current) {
					logger.error(
						'WS-TRACK-MY-OFFER: Payment data timeout reached - no data available',
						{
							timeout: paymentTimeout,
							currentPayments: paymentsRef.current
						}
					);
					setHasError(true);
					setIsLoading(false);
				} else {
					logger.warn('WS-TRACK-MY-OFFER: Payment timeout but cached data exists, continuing');
				}
				// Track timeout for monitoring
				addPaymentTimeoutToNewRelic(paymentTimeout);
				errorTimeoutRef.current = null;
			}, paymentTimeout);

			// Choose event source based on feature flag
			if (usePaymentObjectUpdate) {
				logger.info('WS-TRACK-MY-OFFER: Using PaymentObject:Update as render source');
				eventBus.on('uds:PaymentObject:Update', handlePaymentObjectUpdate);
			} else {
				logger.info('WS-TRACK-MY-OFFER: Using LEGACY PaymentCalcResponse:Update event flow');
				eventBus.on('uds:PaymentCalcResponse:Update', handlePaymentUpdate);

				// Legacy handler for esntial flow
				eventBus.on('uds:paymentUpdate:completed', handlePaymentUpdate);
			}

			// Handle payment errors from UDS
			const handlePaymentError = (errorData) => {
				logger.error(
					'WS-TRACK-MY-OFFER: Received payment error from UDS-LITE',
					{
						error: errorData?.error,
						context: errorData?.context,
						timestamp: errorData?.timestamp
					}
				);

				// Clear existing timeout
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}

				// Hide loader on error
				setHasError(true);
				setIsLoading(false);

				// Track error
				if (window.newrelic && window.newrelic.addPageAction) {
					window.newrelic.addPageAction('paymentError', {
						error: errorData?.error || 'Unknown error',
						context: errorData?.context || 'payment',
						source: 'uds-lite'
					});
				}
			};

			eventBus.on('uds:paymentUpdate:error', handlePaymentError);

			eventBus.on('uds:calcConfigUpdate:request', (configInfo) => {
				startTimeMarker('payment');
				logger.info(
					'WS-TRACK-MY-OFFER -> uds:calcConfigUpdate:request:',
					configInfo
				);
			});

			eventBus.on('uds:calcConfigUpdate:completed', (configInfo) => {
				logger.info(
					'WS-TRACK-MY-OFFER -> uds:calcConfigUpdate:completed:',
					configInfo
				);

				// Flush buffered payment data
				if (usePaymentObjectUpdate && !isInitialCalcDoneRef.current) {
					isInitialCalcDoneRef.current = true;
					flushPendingPayment();
				}
			});

			pubsub?.subscribe(
				'ddc-wallet-integration/esntial:paymentError',
				hideOnEsntialPaymentError
			);

			// Backward compatibility for window events
			const handleWindowPaymentError = (event) => {
				if (event.detail) {
					handlePaymentError(event.detail);
				}
			};

			window.addEventListener(
				'uds:payment:error',
				handleWindowPaymentError
			);

			return () => {
				// Clean up all event listeners
				if (usePaymentObjectUpdate) {
					eventBus.off('uds:PaymentObject:Update', handlePaymentObjectUpdate);
				} else {
					eventBus.off('uds:PaymentCalcResponse:Update', handlePaymentUpdate);
					eventBus.off('uds:paymentUpdate:completed', handlePaymentUpdate);
				}
				eventBus.off('uds:paymentUpdate:error', handlePaymentError);

				pubsub?.unsubscribe(
					'ddc-wallet-integration/esntial:paymentError',
					hideOnEsntialPaymentError
				);

				window.removeEventListener(
					'uds:payment:error',
					handleWindowPaymentError
				);

				// Clear timeout
				if (errorTimeoutRef.current) {
					clearTimeout(errorTimeoutRef.current);
					errorTimeoutRef.current = null;
				}

				// Clear all refs to prevent memory leaks
				pendingPaymentRef.current = null;
				cachedPaymentDataRef.current = null;
				isInitialCalcDoneRef.current = false;
				initialEventCountRef.current = 0;
				hasRenderedCachedRef.current = false;
				paymentsRef.current = null;
			};
		}
		return () => { };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReady, eventBus, paymentTimeout, hideOnEsntialPaymentError, usePaymentObjectUpdate]);

	useEffect(() => {
		paymentsRef.current = udsPayments;

		if (!usePaymentObjectUpdate && udsPayments && Object.keys(udsPayments).length > 0) {
			const result = processLegacyPayment(udsPayments, {
				enableIncentives,
				listingType,
				cachePreloadEnabled: flags[CACHE_PRELOAD_FLAG]
			});

			if (!result) return;

			if (result.payments.length > 0) {
				setPaymentsData(result.payments);
				setHasReceivedData(true);
			} else {
				setPaymentsData([]);
				setHasReceivedData(true);
				setHasError(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [udsPayments, enableIncentives, listingType, usePaymentObjectUpdate]);

	return [isLoading && !hasReceivedData, hasError, hasReceivedData ? paymentsData : null];
};

export default usePaymentData;
